<template>
  <article id="ERROR" class="container-fixed" style="padding-top: 100px">
    <header>
      <h1 class="text-center">
        {{ $t(header) }}
      </h1>
    </header>
    <section class="text-center button-bar" aria-live="assertive">
      <p data-test="message" v-html="mappedMessage"></p>
      <component :is="retryButtonHook" v-bind="retryButtonBindings">
        <button v-if="$route.name === ERROR_RETRY_CODE" type="button" class="btn btn-default" @click="onRetry">{{ $t('common.retry') }}</button>
      </component>
    </section>
    <section v-if="showTechnicalInformation || isDev" data-test="technical-information">
      <dl class="list-desc-grid list-desc-semibold">
        <dt>{{ $t('common.natco') }}:</dt>
        <dd>{{ natco }}</dd>

        <dt>{{ $t('common.environment') }}:</dt>
        <dd>{{ environment }}</dd>

        <dt>{{ $t('common.version') }}:</dt>
        <dd>{{ version }}</dd>

        <dt>{{ $t('common.errorId') }}:</dt>
        <dd>{{ sessionId }}/{{ now }}</dd>

        <dt>
          <button type="button" class="linklike" :aria-expanded="showInternalError" aria-controls="error-history" @click.stop="onRevealHistoryCnt">
            Internal code:
          </button>
        </dt>
        <dd id="error-history">20190814_1601</dd>
      </dl>

      <template v-if="showInternalError">
        <hr />
        <pre v-for="(err, index) in getErrorHistory()" :key="index">{{ err }}</pre>
      </template>
    </section>

    <section class="offset-top-6">
      <ol v-if="footnotes" class="footnotes">
        <li v-for="(item, index) in footnotes" :key="index">{{ item }}</li>
      </ol>
    </section>
  </article>
</template>

<script>
import { mapState } from 'vuex';
import { ERROR, ERROR_FATAL_CODE, ERROR_MEDIUM_CODE, ERROR_RETRY_CODE } from '@route-types';
import mediumErrorCodes from './mediumErrorCodes';
import VTransparent from '@/components/Transparent';
import { errorService } from '@/utils/hookServices';

let errorCache = [];

export default {
  name: 'Error',

  components: {
    VTransparent,
  },

  metaInfo: {
    title: 'Error',
  },

  props: {
    error: {
      type: [Error, Object],
      required: false,
      default: null,
    },

    code: {
      type: String,
      required: false,
      default: 'BACKEND_ERROR',
    },

    errorLevel: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    const data = {
      retryButtonHook: VTransparent,
      retryButtonBindings: {},
    };

    const hookService = errorService(this, data);

    if (this.$route.name === ERROR) {
      errorCache = [];
    }

    const isDev = process.env.NODE_ENV === 'development' && !window.Cypress;

    return {
      ...data,
      showTechnicalInformation: this.$store.getters['system/hasShowTechnicalInformation'],
      isDev,
      ERROR_RETRY_CODE,
      xtcHideRetryButtonErrorLevel: '20062',
      showInternalError: isDev,
      showHistoryCounter: 0,
      header: 'common.headerError',
      now: new Date().getTime(),
      hookService,
    };
  },

  computed: {
    version() {
      return process.env.VUE_APP_VERSION;
    },

    ...mapState('system', ['natco', 'environment', 'sessionId']),

    mappedMessage() {
      if (this.message) {
        return this.message;
      } else if (this.errorLevel && this.errorcodes && this.$te(`errorcodes.${this.errorLevel}`)) {
        return this.$t(`errorcodes.${this.errorLevel}`, this.args);
      } else if (this.$te(`errorcodes.${this.code}`)) {
        return this.$t(`errorcodes.${this.code}`, this.args);
      } else if (this.$te(`substore.${this.code}`)) {
        return this.$t(`substore.${this.code}`, this.args);
      }

      return this.$t('errorcodes.BACKEND_ERROR');
    },

    // we need this for the watcher, if the i18n is loaded later than this error page
    errorcodes() {
      return this.$i18n.messages[this.$i18n.locale].errorcodes;
    },

    message() {
      return this.getLastError()?.data?.errorDetails?.head?.fault?.message;
    },

    args() {
      return this.getLastError()?.data?.args;
    },

    footnotes() {
      return this.getLastError()?.data?.errorDetails?.head?.fault?.footnotes;
    },
  },

  watch: {
    $route: {
      handler: 'parseError',
      immediate: true,
    },
  },

  created() {
    this.$webtrekk.viewPage('backendfehler', 'error.common');
  },

  methods: {
    onRevealHistoryCnt() {
      this.showHistoryCounter += 1;
      if (this.showHistoryCounter >= 5) {
        this.revealHistory();
      }
    },

    revealHistory() {
      this.showInternalError = true;
      errorCache.forEach((e) => {
        // eslint-disable-next-line no-console
        console.log(e);
      });
    },

    onRetry() {
      this.$router.go(-1);
    },

    parseError() {
      if (this.error) {
        // new error
        // set error history
        this.setErrorHistory();

        if (this.isDev) this.revealHistory();
      }

      this.hookService.proxy('parseError', async () => {
        if (this.error?.status === 401) {
          // Detecting timeout and resuming
          await this.$store.dispatch('system/resetAll');
          await this.$store.dispatch('system/setAuthExpired');
          if (this.$route.params?.originalParams?.originalToName) {
            this.$router.push({ name: this.$route.params.originalParams.originalToName, params: this.$route.params.originalParams.originalToParams });
          } else {
            this.onRetry();
          }
          return;
        }

        if (this.error) {
          // new error

          // set new route name
          const name = this.getRouteName();

          // set mapped code
          const mappedCode = this.getMappedCode();

          // replace route
          const exists = (msg) => (msg && this.$te(`errorcodes.${msg}`) ? msg : null);
          const errorLevel = exists(this.error.data?.args?.TextID) || exists(this.error?.data?.message) || exists(this.error.data?.args?.code);

          if (errorLevel) {
            this.$router.replace({
              name,
              params: { code: mappedCode, errorLevel },
            });
          } else {
            this.$router.replace({
              name,
              params: { code: mappedCode },
            });
          }
        }
      });
    },

    getRouteName() {
      if (this.isMediumErrorCode()) {
        return ERROR_MEDIUM_CODE;
      }
      if (this.error?.data?.codeType === 'RETRY') {
        return ERROR_RETRY_CODE;
      }
      return ERROR_FATAL_CODE;
    },

    isMediumErrorCode() {
      // check SubStore codes
      if (mediumErrorCodes.substoreCodes.includes(this.error?.data?.errorDetails?.head?.fault?.code)) {
        return true;
      }
      // check XTC codes
      if (this.error?.data?.message === this.xtcHideRetryButtonErrorLevel) {
        return true;
      }
      if (mediumErrorCodes.xtcCodes.includes(this.error?.data?.code)) {
        return true;
      }
      return false;
    },

    getMappedCode() {
      if (this.error?.data?.code) {
        let mappedCode = this.error.data.code;

        if ((mappedCode === 'SUBSTORE_ERROR' || mappedCode === 'SUBSTORE_ERROR_FATAL') && this.error?.data?.errorDetails?.head?.fault) {
          const { fault } = this.error.data.errorDetails.head;
          if (fault.code) {
            mappedCode += `:${fault.code}`;
          }
        }
        return mappedCode;
      }
      return 'BACKEND_ERROR'; // init request failed (no translations available!)
    },

    setErrorHistory() {
      errorCache.push(this.error);
    },

    getErrorHistory() {
      return errorCache.map((e) => (e?.stack ? e.stack : JSON.stringify(e)));
    },

    getLastError() {
      return errorCache.slice(-1)[0];
    },
  },
};
</script>
